<template>
  <div class="invite-admin">
    <Panel class="mb-5 d-flex flex-column">
      <div class="row d-flex justify-content-between align-items-center">
        <Subheader
          class="col-6 col-lg-9"
          :text="$t('web_invite-admin_add-title', 'Add admin')"
        />
        <HeraSearchInput
          v-model="userFilter"
          :placeholder="$t('web_invite-admin-search-placeholder', 'Search...')"
          class="col-6 col-lg-3 search-field"
          :debounce="700"
          @input="onSearch"
        />
      </div>
      <div class="text-muted pb-2">
        {{ $t('_web_invite_admins_selected-label', 'Selected users:') }}
        {{ selectedItems.length }}
      </div>
      <div class="list">
        <Paginated
          v-if="filterUsersList.length"
          v-bind="{
            totalItems,
            hidePagination: !filterUsersList.length,
            pageSize,
            paginationProps: {
              isDark: true,
            },
            inContainer: true,
          }"
          class="h-100 w-auto pagination-smaller"
          @change="fetchList"
        >
          <div class="col">
            <ul class="list-group list-group-flush bg-transparent w-100">
              <li
                v-for="user in filterUsersList"
                :key="user._id"
                class="border-0 bg-transparent row no-gutters d-flex justify-content-between align-items-center py-3 h-pr-3"
                @click="toggleUserSelection(user._id)"
              >
                <ListItemAccount :model="user" class="col-11" />
                <div
                  class="col-1 d-flex align-items-center justify-content-end"
                >
                  <HeraFormCheckbox v-model="selectedItems" :val="user._id" />
                </div>
              </li>
            </ul>
          </div>
        </Paginated>
        <template v-else>
          <HeraDummyList
            v-if="isLoading && !usersList.length"
            icon-name="dummy/members"
            size="200"
            :header-text="
              $t('_web_invite-admin_fetching_users', `Fetching list of users`)
            "
          />
          <HeraDummyList
            v-else-if="userFilter"
            icon-name="dummy/members"
            size="200"
            :header-text="
              $t('_web_invite-admin_no_users', `We couldn't find anybody`)
            "
          />
          <HeraDummyList
            v-else
            icon-name="search"
            size="24"
            :header-text="
              $t(
                '_web_invite-admin_start-message',
                `Start typing to find users`
              )
            "
          />
        </template>
      </div>
    </Panel>
    <div class="d-flex justify-content-center mb-4 mb-lg-0">
      <HeraButton
        class="btn-default h-mr-5"
        :label="$t('_web_invite-admin_skip', 'Cancel')"
        @click="goToSettings"
      />
      <HeraButton
        class="btn-primary"
        :label="$t('_web_invite-admin_done-button', 'Add Admin')"
        @click="setAdmin"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import ROUTER_NAMES_ACCOUNT from '@/constants/router/account';

import HeraFormCheckbox from '@/components/form/HeraFormCheckbox';
import HeraSearchInput from '@/components/molecules/form/HeraSearchInput';
import HeraDummyList from '@/components/common/HeraDummyList';
import Panel from '@/components/atoms/Panel';
import Subheader from '@/components/atoms/Subheader';
import Paginated from '@/components/common/Paginated';
import listMixin from '@/mixins/paginatedLists';
import HeraButton from '@/components/atoms/HeraFormBtn';
import flip from '@/directives/flip';
import ListItemAccount from '@/components/common/ListItemAccount';
import AccountModel from '@/models/Account';

export default {
  name: 'InviteAdmin',
  directives: {
    flip,
  },
  components: {
    HeraFormCheckbox,
    HeraSearchInput,
    HeraDummyList,
    Paginated,
    HeraButton,
    ListItemAccount,
    Panel,
    Subheader,
  },
  mixins: [...listMixin],
  props: {
    adminsList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      usersList: [],
      selectedItems: [],
      userFilter: '',
      showCongrats: false,
    };
  },
  computed: {
    ...mapState('my', ['account']),
    filterUsersList() {
      return this.usersList.filter(
        item => item.type === 'profile' && !this.adminsList.includes(item.login)
      );
    },
  },
  created() {
    this.fetchList();
  },

  methods: {
    ...mapActions([
      'toggleLockScreen',
      'setOrganizationMember',
      'fetchPlayers',
      'errorNotify',
      'fetchOrgs',
    ]),
    goToSettings() {
      this.$router.push({ name: ROUTER_NAMES_ACCOUNT.SETTINGS });
    },
    toggleUserSelection(_id) {
      const index = this.selectedItems.findIndex(l => l === _id);
      if (index === -1) {
        this.selectedItems.push(_id);
      } else {
        this.selectedItems.splice(index, 1);
      }
    },
    onSearch() {
      this.pageIndex = 0;
      this.usersList = [];
      this.fetchList();
    },
    fetchList(newPage) {
      this.pageIndex = newPage;
      return this.getPageData(
        'usersList',
        this.fetchPlayers(
          this.paramsWithPageNumber({
            contains: this.userFilter,
            needCommit: false,
          })
        ),
        {
          map: user =>
            new AccountModel(user.type, user.id || user.login).update(user),
        }
      );
    },
    setAdmin() {
      this.isLoading = true;
      let promisesList = [];
      this.selectedItems.forEach(login => {
        promisesList.push(
          this.setOrganizationMember({
            accountId: this.account._id,
            orgId: login,
          })
        );
      });
      if (!promisesList.length) {
        return;
      }
      this.toggleLockScreen(true);
      Promise.all(promisesList)
        .then(() => {
          this.isLoading = false;
          this.showCongrats = true;
          this.$emit('updateAdminsList', this.preparedSelectedItems());
          this.$emit('close');
          this.clearSelected();
        })
        .catch(error => {
          this.isLoading = false;
          this.errorNotify(error.message);
        })
        .then(() => {
          this.toggleLockScreen(false);
          this.goToSettings();
        });
    },
    onCongratsEnd() {
      this.showCongrats = false;
      this.$router.go(); //refresh page or emit update event
    },
    clearSelected() {
      this.selectedItems = [];
    },
    preparedSelectedItems() {
      return this.usersList.filter(
        ({ login }) => this.selectedItems.indexOf(login) !== -1
      );
    },
  },
};
</script>

<style lang="scss">
.invite-admin {
  .btn {
    min-width: 9rem;
  }
}
@media (max-width: 767px) {
  .pagination-smaller {
    .pagination-class {
      .hand-smallscreen-r {
        margin-right: 0px !important;
        @include auto-rtl(margin-right, 0px !important);
      }
      .hand-smallscreen-l {
        margin-left: 0px !important;
        @include auto-rtl(margin-left, 0px !important);
      }
    }
  }
}
</style>
